import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import { LoadingPageIcon } from "utils/constant";
import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { getDistributedTowerDataByAdminUnit } from "services/legalAwarenessService";
import UploadWoredaReport from "pages/lawImplementation/fragments/uploadWoredaReport";
import ZoneImplementationGuide from "./fragments/zoneImplementationGuide";

const SingleWoredaAwareness = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const contextData = useAuth();
    const { showToast } = useToast();

    const stateWoreda = (location.state as any)?.woreda;
    const [woreda, setWoreda] = useState<any>(stateWoreda || null);
    const [isLoading, setIsLoading] = useState(!stateWoreda);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getDistributedTowerDataByAdminUnit(contextData?.user?.admin_unit || "");
                const match = Array.isArray(data) ? data.find((w: any) => String(w.id) === String(id)) : null;
                if (match) setWoreda(match);
            } catch (error) {
                console.error("Error fetching woreda data:", error);
                showToast(t("FailedToFetchLegalAwarenessRequest") || "Failed to fetch request.", "error");
            } finally {
                setIsLoading(false);
            }
        };
        // Always refresh to get the latest report state; keep state data meanwhile.
        fetchData();
    }, [id, contextData?.user?.admin_unit, showToast, t, reload]);

    if (isLoading && !woreda)
        return (
            <div className="flex justify-center items-center h-64">{LoadingPageIcon}</div>
        );

    if (!woreda)
        return (
            <div className="flex justify-center items-center h-64 text-gray-500">
                {t("RequestNotFound") || "Request not found."}
            </div>
        );

    const sortByDateDesc = (a: any, b: any) => {
        const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
    };
    const allDocs = woreda.documents || [];
    // Show the same legal awareness file the zone users see (distrbuted_zone_awarness),
    // falling back to the tower/original awareness file if that's how it was stored.
    const zoneAwarenessDocs = allDocs.filter((doc: any) => doc.document_type === "distrbuted_zone_awarness");
    const fallbackAwarenessDocs = allDocs.filter(
        (doc: any) => doc.document_type === "distrbuted_tower_awarness" || doc.document_type === "legal_awareness_document_file"
    );
    const awarenessDocs = [...(zoneAwarenessDocs.length ? zoneAwarenessDocs : fallbackAwarenessDocs)].sort(sortByDateDesc);
    const latestAwarenessDoc = awarenessDocs[0];
    const reportDocs = (woreda.documents || []).filter(
        (doc: any) => (doc.document_type || "").includes("report")
    );
    const hasReport = !!woreda.report_summary;
    const baseUrl = `${process.env.REACT_APP_API_BASE_URL}legal_awareness/legal-awareness-documents/document`;

    return (
        <div className="flex h-full bg-gray-100 p-2">
            <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
                <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md overflow-hidden">
                    {/* Governmental header */}
                    <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <button onClick={() => navigate("/legal-awareness-creation")} className="text-white/80 hover:text-white">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                                <h1 className="text-xl font-semibold text-white">{t("LegalAwarenessForWoredaImplementation") || "Legal Awareness for Woreda Implementation"}</h1>
                            </div>
                        </div>
                        <StatusChip status={woreda.status} />
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Order / instruction from zone */}
                        {woreda.legal_awareness_order && (
                            <div className="border border-gray-200 rounded-lg p-4">
                                <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-1">{t("LegalAwarenessOrder") || "Legal Awareness Order"}</p>
                                <p className="text-sm text-[#606060]">{woreda.legal_awareness_order}</p>
                            </div>
                        )}

                        {/* Legal Awareness file distributed to the woreda */}
                        <div className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <svg className="w-5 h-5 text-[#0A1D39]" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                                <p className="font-semibold text-[#0A1D39]">{t("LegalAwarenessFile") || "Legal Awareness File"}</p>
                            </div>
                            {latestAwarenessDoc ? (
                                <DocumentViewerModal documents={[latestAwarenessDoc]} baseUrl={baseUrl} />
                            ) : (
                                <p className="text-sm text-gray-400 italic">{t("NoFileAttached") || "No file attached."}</p>
                            )}
                        </div>

                        {/* Report: show submitted report or upload action */}
                        <div className="border-2 rounded-lg p-4 border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                                <p className="font-semibold text-[#0A1D39]">{t("WoredaReport") || "Woreda Report"}</p>
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${hasReport ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                    {hasReport ? (t("Submitted") || "Submitted") : (t("NotSubmitted") || "Not Submitted")}
                                </span>
                            </div>
                            {hasReport ? (
                                <>
                                    <p className="text-sm text-[#606060] mb-3">{woreda.report_summary}</p>
                                    {reportDocs.length > 0 && (
                                        <DocumentViewerModal documents={reportDocs} baseUrl={baseUrl} />
                                    )}
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setIsReportOpen(true)}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#0A1D39] text-white text-sm font-semibold shadow-sm hover:bg-[#13294d] transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    {t("UploadReport") || "Upload Report"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ZoneImplementationGuide variant="woreda" data={woreda} />

            {isReportOpen && (
                <UploadWoredaReport
                    request={woreda}
                    setLegalResearch={setWoreda}
                    isOpen={isReportOpen}
                    onClose={() => { setIsReportOpen(false); setReload((r) => r + 1); }}
                    woredaId={woreda.id}
                    woredaName={woreda.admin_unit_name}
                />
            )}
        </div>
    );
};

export default SingleWoredaAwareness;
